import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCog, UserPlus, Trash2, Shield, Loader2, Crown, KeyRound, Mail } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/roles";

const ASSIGNABLE = [
    { id: "admin", label: "Admin", desc: "Full access except staff management" },
    { id: "tech_support", label: "Tech Support", desc: "Users + Live Now (no revenue/config)" },
    { id: "management", label: "Management", desc: "Business analytics + overview" },
];

export default function AdminStaff() {
    const [staff, setStaff] = useState(null);
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("promote");
    const [form, setForm] = useState({ email: "", role: "tech_support", first_name: "", last_name: "", password: "" });
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/admin/staff").then(({ data }) => setStaff(data.staff));
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.email.trim()) return toast.error("Email is required.");
        if (mode === "create" && form.password.length < 8) return toast.error("Temporary password must be 8+ chars with letters & numbers.");
        setBusy(true);
        try {
            await api.post("/admin/staff", { ...form, email: form.email.trim(), mode });
            toast.success(mode === "create" ? "Staff account created." : "Member promoted to staff.");
            setOpen(false);
            setForm({ email: "", role: "tech_support", first_name: "", last_name: "", password: "" });
            load();
        } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not add staff."); }
        finally { setBusy(false); }
    };

    const changeRole = async (id, role) => {
        try { await api.patch(`/admin/staff/${id}`, { role }); toast.success("Role updated."); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not update role."); }
    };

    const revoke = async (id) => {
        try { await api.delete(`/admin/staff/${id}`); toast("Staff access revoked."); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not revoke."); }
    };

    if (!staff) return <Loader label="Loading staff" />;

    return (
        <div className="ww-fade-up" data-testid="admin-staff">
            <PageHeader title="Staff Management" subtitle="Add internal team members (Admin, Tech Support, Management) to help run the platform.">
                <button onClick={() => setOpen(true)} data-testid="add-staff-btn"
                    className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center gap-2">
                    <UserPlus className="w-4 h-4" /> Add Staff
                </button>
            </PageHeader>

            <div className="bg-surface border border-border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                        <th className="text-left font-semibold px-5 py-3">Member</th>
                        <th className="text-left font-semibold px-5 py-3">Role</th>
                        <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Access</th>
                        <th className="text-right font-semibold px-5 py-3">Action</th>
                    </tr></thead>
                    <tbody>
                        {staff.map((s) => (
                            <tr key={s.id} className="border-b border-border/50" data-testid={`staff-row-${s.id}`}>
                                <td className="px-5 py-3">
                                    <div className="text-white flex items-center gap-1.5">{s.is_owner && <Crown className="w-3.5 h-3.5 text-volt" />}{s.name}
                                        {s.must_change_password && <span className="text-[10px] text-warn font-mono ml-1">TEMP PW</span>}</div>
                                    <div className="text-xs text-muted-foreground font-mono">{s.email}</div>
                                </td>
                                <td className="px-5 py-3">
                                    {s.is_owner ? (
                                        <span className="inline-flex items-center gap-1.5 text-sm text-volt"><Crown className="w-3.5 h-3.5" /> Owner</span>
                                    ) : (
                                        <Select value={s.role} onValueChange={(v) => changeRole(s.id, v)}>
                                            <SelectTrigger className="w-[150px] h-9 bg-obsidian border-border rounded-sm" data-testid={`staff-role-${s.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent className="bg-surface2 border-border">
                                                {ASSIGNABLE.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    )}
                                </td>
                                <td className="px-5 py-3 hidden md:table-cell">
                                    <div className="flex flex-wrap gap-1">
                                        {(s.caps || []).map((c) => <span key={c} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">{c}</span>)}
                                    </div>
                                </td>
                                <td className="px-5 py-3 text-right">
                                    {s.is_owner ? <span className="text-xs text-muted-foreground">—</span> : (
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <button data-testid={`revoke-staff-${s.id}`} className="text-muted-foreground hover:text-alert"><Trash2 className="w-4 h-4" /></button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent className="bg-surface2 border-border">
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Revoke {s.name}'s staff access?</AlertDialogTitle>
                                                    <AlertDialogDescription>They become a regular player. Their account and data are kept.</AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel className="bg-transparent border-border">Cancel</AlertDialogCancel>
                                                    <AlertDialogAction onClick={() => revoke(s.id)} data-testid={`revoke-confirm-${s.id}`} className="bg-alert text-white hover:bg-[#ff4d7a]">Revoke</AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-surface border-border max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2"><UserCog className="w-5 h-5 text-volt" /> Add Staff Member</DialogTitle>
                        <DialogDescription className="text-xs">Promote an existing user or create a new staff account.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-2">
                            <button onClick={() => setMode("promote")} data-testid="mode-promote"
                                className={`h-10 rounded-sm border text-sm font-semibold flex items-center justify-center gap-1.5 ${mode === "promote" ? "border-volt bg-volt/10 text-volt" : "border-border text-muted-foreground"}`}>
                                <Mail className="w-4 h-4" /> Promote Existing
                            </button>
                            <button onClick={() => setMode("create")} data-testid="mode-create"
                                className={`h-10 rounded-sm border text-sm font-semibold flex items-center justify-center gap-1.5 ${mode === "create" ? "border-volt bg-volt/10 text-volt" : "border-border text-muted-foreground"}`}>
                                <KeyRound className="w-4 h-4" /> Create New
                            </button>
                        </div>
                        <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@whitewater.ai" data-testid="staff-email"
                            className="bg-obsidian border-border rounded-sm h-10" />
                        {mode === "create" && (
                            <>
                                <div className="grid grid-cols-2 gap-2">
                                    <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name" data-testid="staff-first" className="bg-obsidian border-border rounded-sm h-10" />
                                    <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" data-testid="staff-last" className="bg-obsidian border-border rounded-sm h-10" />
                                </div>
                                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temporary password (8+ chars, letters & numbers)" data-testid="staff-password"
                                    className="bg-obsidian border-border rounded-sm h-10 font-mono" />
                            </>
                        )}
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Role</label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger className="bg-obsidian border-border rounded-sm h-10 mt-1.5" data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-surface2 border-border">
                                    {ASSIGNABLE.map((r) => <SelectItem key={r.id} value={r.id}>{r.label} — <span className="text-muted-foreground">{r.desc}</span></SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <button onClick={submit} disabled={busy} data-testid="staff-submit"
                            className="w-full h-11 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center justify-center gap-2 disabled:opacity-50">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                            {mode === "create" ? "Create Staff Account" : "Promote to Staff"}
                        </button>
                        {mode === "create" && <p className="text-[11px] text-muted-foreground">Share the temporary password with them — they'll be asked to change it.</p>}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
